import { isEmpty, isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { Menu, Submenu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { useToast } from '@shared/components/Toast';
import { View } from '@shared/components/View';
import { TitledWorkflowTemplate, getEntityTitle } from '@shared/data/entity';
import {
  RelatedEntity,
  RelatedEntityField,
  RelatedItem,
} from '@shared/data/entity/fields/RelatedEntityField';
import { FieldRecord } from '@shared/scenes/entity/types';
import { getComponentLabelWithFallback } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useCreateRelatedEntity } from './useCreateRelatedEntity';
import { useRelatedEntitySearch } from './useRelatedEntitySearch';
import { useWorkflowSupportsQuickCreate } from './useWorkflowSupportsQuickCreate';
import styles from './RelatedEntityPicker.module.css';

/**
 * RelatedEntityPicker
 *
 * The card picker `RelatedEntityEditField` opens from its field. Mounted only while open: the menu
 * opens on mount, and closing it calls `onClose`.
 *
 * Searches the related workflow on the server, with load more, and titles the list with the
 * selected count. When the related workflow supports it, "Create Card" at the bottom opens a
 * form prefilled with the search text (or "New Card") to create a card and select it.
 */

interface Props {
  entities: RelatedEntity[];
  onChange(values: RelatedEntity[]): void;
  onClose(): void;
  anchorName: string;
  component: API.RelatedCardComponent;
  field: RelatedEntityField | null;
  fields: FieldRecord;
  canCreateNew: boolean;
}

const EMPTY_ARRAY: RelatedItem[] = [];

export function RelatedEntityPicker({
  entities,
  field,
  anchorName,
  onChange,
  onClose,
  component,
  fields,
  canCreateNew,
}: Props) {
  const {
    keyword,
    onSearch,
    refetch,
    searchResults,
    loading,
    hasMore,
    loadMore,
    secondaryTextById,
    workflowTemplate,
  } = useRelatedEntitySearch(component, fields);
  const items = field?.items ?? EMPTY_ARRAY;
  const selectedIds = new Set(items.map(({ id }) => id));
  const query = keyword.trim();
  const isDesktop = useIsDesktop();

  const addNewEntity = useCreateRelatedEntity({
    workflowTemplateId: component.workflowTemplateId,
  });
  const workflowSupportsQuickCreate = useWorkflowSupportsQuickCreate({
    workflowTemplateId: component.workflowTemplateId,
  });
  const allowCreation = canCreateNew && workflowSupportsQuickCreate;

  const getTitle = useCallback(
    (entity: RelatedEntity) => getEntityTitle(entity, workflowTemplate) ?? '',
    [workflowTemplate],
  );

  const onToggle = (entity: RelatedEntity) => {
    if (selectedIds.has(entity.id)) {
      onChange(entities.filter(({ id }) => id !== entity.id));
      return;
    }
    onChange([...entities, entity]);
  };

  // The search doesn't know about the new card: clearing it refetches only when the text changes,
  // so reload it outright. addNewEntity resolves with null when the create fails.
  const onCreate = async (name: string) => {
    const newEntity = await addNewEntity(name);
    if (isNil(newEntity)) {
      throw new Error('Could not create the related card');
    }
    onChange([...entities, newEntity]);
    onSearch('');
    // Not awaited: the card exists and is selected; a failed reload shouldn't report the create as failed
    void refetch();
  };

  const emptyLabel = isEmpty(query)
    ? i18n('card.blocks.cardsInput.emptyState')
    : i18n('common.empty');
  const label = getComponentLabelWithFallback(component);
  const selectedTitle = `${items.length} ${i18n('card.blocks.cardsInput.selected.default')}`;

  return (
    <Menu
      isOpen
      onOpenChange={isOpen => !isOpen && onClose()}
      anchorName={anchorName}
      closeOnSelect={false}
    >
      <Menu.Content mobileSheet editFieldMenu className={styles.relatedCardMenu} aria-label={label}>
        <Menu.TitleBar title={label} endContent={<Menu.CloseButton />} mobileOnly />
        <View flex>
          {/* On mobile the title bar already shows the label, so the count takes its place */}
          <Menu.SearchableOptions
            title={isDesktop ? label : selectedTitle}
            titleEnd={isDesktop ? selectedTitle : undefined}
            filter={keyword}
            setFilter={onSearch}
            options={searchResults}
            loading={loading && isEmpty(searchResults)}
            emptyLabel={emptyLabel}
            getItemKey={entity => entity.id}
            getItemLabel={getTitle}
            getItemContent={entity => (
              <EntityPickerItem
                entity={entity}
                workflowTemplate={workflowTemplate}
                secondaryText={secondaryTextById.get(entity.id)}
              />
            )}
            isItemSelected={entity => selectedIds.has(entity.id)}
            onSelect={onToggle}
            multiple
            footer={
              hasMore && (
                <Menu.LoadMore onClick={loadMore} loading={loading && !isEmpty(searchResults)} />
              )
            }
          />
        </View>
        {allowCreation && <CreateCard query={query} onCreate={onCreate} />}
      </Menu.Content>
    </Menu>
  );
}

/**
 * "Create Card “<query>”" (or "Create Card" with an empty search) opens a form prefilled with the
 * search text or "New Card". Duplicate names are allowed.
 */
function CreateCard({
  query,
  onCreate,
}: {
  query: string;
  onCreate: (name: string) => Promise<void>;
}) {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const label = i18n('card.blocks.cardsInput.createCard');
  const itemLabel = isEmpty(query) ? label : `${label} “${query}”`;

  const create = async (value: string) => {
    try {
      await onCreate(value.trim());
      setIsOpen(false);
    } catch (err) {
      toast({ style: 'warning', message: i18n('errors.somethingWentWrong') });
      console.error(String(err));
    }
  };

  return (
    <>
      <Menu.Separator />
      <Submenu isOpen={isOpen} onOpenChange={setIsOpen}>
        <Submenu.Trigger>
          <Menu.Item
            icon="plus"
            aria-label={itemLabel}
            onClick={() =>
              setName(isEmpty(query) ? i18n('card.blocks.cardsInput.newCardName') : query)
            }
          >
            <Menu.CreateLabel label={label} query={query} />
          </Menu.Item>
        </Submenu.Trigger>
        <Submenu.Content mobileSheet>
          <Menu.TitleBar endContent={<Menu.CloseButton />} mobileOnly />
          <Menu.NameForm
            label={label}
            value={name}
            setValue={setName}
            buttonLabel={i18n('common.create')}
            canSave={!isEmpty(name.trim())}
            onSave={create}
          />
        </Submenu.Content>
      </Submenu>
    </>
  );
}

function EntityPickerItem({
  entity,
  workflowTemplate,
  secondaryText,
}: {
  entity: RelatedEntity;
  workflowTemplate?: TitledWorkflowTemplate;
  secondaryText?: string;
}) {
  return (
    <View flex>
      <Text size="body1">{getEntityTitle(entity, workflowTemplate) ?? ''}</Text>
      {!isNil(secondaryText) && (
        <Text size="body2" inactive numberOfLines={1}>
          {secondaryText}
        </Text>
      )}
    </View>
  );
}
